import { DialogTitle } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { workspaceIntegrations } from "apis-v2/callables";
import { Button } from "js/Components/Button";
import { Input } from "js/Components/Input";
import { WithLabel } from "js/Components/WithLabel";
import WorkspaceController from "js/controllers/WorkspaceController";
import { BeautifulDialog, DialogContent, ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";

import { Icon, Section, SectionsContainer, Text } from "./DialogComponents";

const Heading = styled.div`
    font-weight: 600;
`;

const ConnectionSummary = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 32px;
    padding: 4px 10px;
    background: #f2f2f2;
`;

const ConnectionStatus = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-weight: 600;
`;

const MaskedKey = styled.div`
    flex: 1;
    min-width: 0;
    color: #333;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DisconnectButton = styled(props => <Button {...props} warning unframed small />)`
    flex-shrink: 0;
`;

const PrimaryButton = styled(props => <Button {...props} blue />)`
    align-self: flex-end;
`;

const BodyText = styled(Text)`
    color: #666;
`;

export function BrandfolderDialog({ closeDialog, onConnected }: { closeDialog: () => void; onConnected?: () => void }) {
    const workspaceId = WorkspaceController.workspaceId;
    const [connected, setConnected] = useState(false);
    const [keySuffix, setKeySuffix] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [apiKeyUpdated, setApiKeyUpdated] = useState(false);

    useEffect(() => {
        workspaceIntegrations.getBrandfolderConnection({ workspaceId })
            .then(status => {
                setConnected(status.connected);
                setKeySuffix(status.keySuffix || "");
            })
            .catch(() => setError("Could not load your Brandfolder connection."));
    }, [workspaceId]);

    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        setError("");
        try {
            await action();
        } catch (err) {
            setError(err.message || "Brandfolder request failed.");
        } finally {
            setBusy(false);
        }
    };

    const save = () => run(async () => {
        const status = await workspaceIntegrations.saveBrandfolderConnection({ workspaceId, apiKey });
        setApiKeyUpdated(connected);
        setConnected(true);
        setKeySuffix(status.keySuffix);
        setApiKey("");
        onConnected?.();
    });

    const disconnect = () => ShowConfirmationDialog({
        title: "Disconnect Brandfolder?",
        message: "You will no longer be able to import from Brandfolder until you reconnect your account.",
        okButtonLabel: "Disconnect",
        acceptCallback: () => run(async () => {
            await workspaceIntegrations.removeBrandfolderConnection({ workspaceId });
            setConnected(false);
            setKeySuffix("");
        })
    });

    return (<BeautifulDialog maxWidth="xs" closeDialog={closeDialog} closeButton>
        <DialogTitle>{connected ? "Brandfolder connection" : "Connect Brandfolder"}</DialogTitle>
        <DialogContent>
            <SectionsContainer>
                {connected && <Section>
                    <ConnectionSummary>
                        <ConnectionStatus><Icon style={{ color: "#008000" }}>check</Icon>Connected</ConnectionStatus>
                        <MaskedKey>••••{keySuffix}</MaskedKey>
                        <DisconnectButton disabled={busy} onClick={disconnect}>Disconnect</DisconnectButton>
                    </ConnectionSummary>
                </Section>}
                <Section>
                    {connected && <Heading>Update API key</Heading>}
                    <BodyText>{connected
                        ? "Use a different API key to connect a different Brandfolder account."
                        : "Enter your personal Brandfolder API key to connect your Brandfolder account to Beautiful.ai."}
                    </BodyText>
                    <WithLabel label={connected ? "New API key" : "API key"} above gap={5}>
                        <Input aria-label="Brandfolder API key" type="password" autoComplete="off" width="100%" disabled={busy} value={apiKey} onChange={event => {
                            setApiKeyUpdated(false);
                            setApiKey(event.target.value);
                        }} placeholder={connected ? "Enter your new API key" : "Enter your API key"} />
                    </WithLabel>
                    <PrimaryButton showSpinner={busy} disabled={busy || !apiKey.trim()} onClick={save}>{connected ? (apiKeyUpdated ? "Updated" : "Update API key") : "Connect"}</PrimaryButton>
                </Section>
                {error && <Text warning role="alert">{error}</Text>}
            </SectionsContainer>
        </DialogContent>
    </BeautifulDialog>);
}
